<template>
  <div class="concrete-pour-tracker">
    <!-- Header -->
    <div class="tracker-header d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-2">
        <div class="header-icon">
          <i class="bi bi-building"></i>
        </div>
        <div>
          <h4 class="mb-0">Concrete Pour Planner</h4>
          <small class="text-muted">Find safe pouring windows for your job sites</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <div class="btn-group btn-group-sm">
          <button 
            class="btn" 
            :class="unitGroup === 'metric' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setUnitGroup('metric')"
          >
            °C
          </button>
          <button 
            class="btn" 
            :class="unitGroup === 'us' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setUnitGroup('us')"
          >
            °F
          </button>
        </div>
      </div>
    </div>

    <!-- Sign-in Required -->
    <div v-if="!hasSession" class="alert alert-warning d-flex align-items-center">
      <i class="bi bi-lock me-3 fs-4"></i>
      <div class="flex-grow-1">
        <strong>Sign in required</strong>
        <p class="mb-0 small">Please sign in to access weather data for pour planning.</p>
      </div>
      <a class="btn btn-primary btn-sm" href="#" onclick="window.dialogManager?.showSignIn(); return false;">
        <i class="bi bi-box-arrow-in-right me-1"></i>Sign In
      </a>
    </div>

    <template v-else>
      <!-- Main Layout -->
      <div class="row g-4">
        <!-- Left Column: Setup -->
        <div class="col-lg-4">
          <!-- Job Sites Panel -->
          <div class="card mb-3">
            <div class="card-header py-2">
              <h6 class="mb-0">
                <i class="bi bi-geo-alt me-2"></i>Job Sites
              </h6>
            </div>
            <div class="card-body">
              <ExceptionLocationPanel 
                v-model="locations"
                label=""
                itemLabel="job site"
                icon="bi bi-building"
                placeholder="Site address or coordinates..."
                maxHeight="200px"
              />
            </div>
          </div>

          <!-- Date Range -->
          <div class="card mb-3">
            <div class="card-header py-2">
              <h6 class="mb-0">
                <i class="bi bi-calendar3 me-2"></i>Planning Period
              </h6>
            </div>
            <div class="card-body">
              <div class="btn-group btn-group-sm w-100 mb-2">
                <button 
                  v-for="period in datePeriods" 
                  :key="period.value"
                  class="btn"
                  :class="startPeriod === period.value ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange(period.value)"
                >
                  {{ period.label }}
                </button>
              </div>
              <small class="text-muted">
                <i class="bi bi-info-circle me-1"></i>
                Checking {{ dateRangeDisplay }}
              </small>
            </div>
          </div>

          <!-- Pour Conditions -->
          <div class="card mb-3">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <i class="bi bi-sliders me-2"></i>Pour Conditions
              </h6>
              <button 
                class="btn btn-link btn-sm p-0 text-decoration-none"
                @click="showAdvancedSettings = !showAdvancedSettings"
              >
                {{ showAdvancedSettings ? 'Simple' : 'Advanced' }}
                <i class="bi ms-1" :class="showAdvancedSettings ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
            </div>
            <div class="card-body">
              <!-- Temperature Range -->
              <div class="condition-group mb-3">
                <label class="form-label small fw-semibold">
                  <i class="bi bi-thermometer-half me-1"></i>
                  Safe Temperature Range
                </label>
                <div class="d-flex align-items-center gap-2">
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">Min</span>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="thresholds.minTemp"
                      @change="updateCriteria"
                    >
                    <span class="input-group-text">{{ tempUnit }}</span>
                  </div>
                  <span class="text-muted">to</span>
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">Max</span>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="thresholds.maxTemp"
                      @change="updateCriteria"
                    >
                    <span class="input-group-text">{{ tempUnit }}</span>
                  </div>
                </div>
                <small class="text-muted">
                  Standard: {{ unitGroup === 'metric' ? '5°C - 30°C' : '40°F - 86°F' }}
                </small>
              </div>

              <!-- Rain Threshold -->
              <div class="condition-group mb-3">
                <label class="form-label small fw-semibold">
                  <i class="bi bi-cloud-rain me-1"></i>
                  Max Rain Probability
                </label>
                <div class="d-flex align-items-center gap-2">
                  <input 
                    type="range" 
                    class="form-range flex-grow-1" 
                    v-model.number="thresholds.maxPrecipProb"
                    min="0" max="100" step="5"
                    @change="updateCriteria"
                  >
                  <span class="badge bg-secondary" style="min-width: 50px;">{{ thresholds.maxPrecipProb }}%</span>
                </div>
              </div>

              <!-- Advanced Settings -->
              <template v-if="showAdvancedSettings">
                <!-- Wind Threshold -->
                <div class="condition-group mb-3">
                  <label class="form-label small fw-semibold">
                    <i class="bi bi-wind me-1"></i>
                    Max Wind Gust
                  </label>
                  <div class="input-group input-group-sm">
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="thresholds.maxWindGust"
                      @change="updateCriteria"
                    >
                    <span class="input-group-text">{{ windUnit }}</span>
                  </div>
                  <small class="text-muted">
                    High winds affect surface finishing and curing
                  </small>
                </div>

                <!-- Snow -->
                <div class="condition-group mb-3">
                  <div class="form-check form-switch">
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      id="snowCheck"
                      v-model="thresholds.noSnow"
                      @change="updateCriteria"
                    >
                    <label class="form-check-label small" for="snowCheck">
                      <i class="bi bi-snow me-1"></i>
                      No snow expected
                    </label>
                  </div>
                </div>

                <!-- Hot Weather Precautions -->
                <div class="condition-group mb-3">
                  <label class="form-label small fw-semibold">
                    <i class="bi bi-thermometer-sun me-1 text-danger"></i>
                    Hot Weather Warning
                  </label>
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">Above</span>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="thresholds.hotWeatherTemp"
                      @change="updateCriteria"
                    >
                    <span class="input-group-text">{{ tempUnit }}</span>
                  </div>
                  <small class="text-muted">
                    Requires special hot weather concrete procedures
                  </small>
                </div>

                <!-- Severe Cold -->
                <div class="condition-group mb-3">
                  <label class="form-label small fw-semibold">
                    <i class="bi bi-thermometer-snow me-1 text-info"></i>
                    Cold Weather Alert (Blankets/Heating)
                  </label>
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">Below</span>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="thresholds.coldWeatherTemp"
                      @change="updateCriteria"
                    >
                    <span class="input-group-text">{{ tempUnit }}</span>
                  </div>
                  <small class="text-muted">
                    Requires heated enclosures or insulating blankets
                  </small>
                </div>

                <!-- Storm Risk -->
                <div class="condition-group">
                  <div class="form-check form-switch mb-2">
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      id="stormRiskCheck"
                      v-model="thresholds.stormRisk"
                      @change="updateCriteria"
                    >
                    <label class="form-check-label small fw-semibold" for="stormRiskCheck">
                      <i class="bi bi-cloud-lightning-rain me-1 text-warning"></i>
                      Severe Weather Alert
                    </label>
                  </div>
                  <div v-if="thresholds.stormRisk" class="d-flex align-items-center gap-2 ms-4">
                    <input 
                      type="range" 
                      class="form-range flex-grow-1" 
                      v-model.number="thresholds.stormRiskThreshold"
                      min="10" max="80" step="10"
                      @change="updateCriteria"
                    >
                    <span class="badge bg-warning text-dark" style="min-width: 50px;">{{ thresholds.stormRiskThreshold }}%</span>
                  </div>
                  <small class="text-muted">
                    Thunderstorms, hail, and severe conditions
                  </small>
                </div>
              </template>
            </div>
          </div>

          <!-- Run Button -->
          <button 
            class="btn btn-success w-100"
            :disabled="!canRun || isRunning"
            @click="runExceptionCheck"
          >
            <span v-if="isRunning">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Checking Weather...
            </span>
            <span v-else>
              <i class="bi bi-search me-2"></i>
              Find Pour Windows
            </span>
          </button>
        </div>

        <!-- Right Column: Results -->
        <div class="col-lg-8">
          <!-- Results Summary -->
          <ExceptionResultsSummary 
            v-if="hasRun"
            :results="locationResults"
            :isRunning="isRunning"
            :completedCount="completedCount"
            locationLabel="Job Site"
            okLabel="Safe to Pour"
            warningLabel="Caution"
            dangerLabel="No Pour"
            nextIssueLabel="Next Unsafe Day"
            class="mb-4"
          />

          <!-- View Toggle -->
          <div v-if="hasRun && !isRunning" class="d-flex justify-content-between align-items-center mb-3">
            <div class="btn-group btn-group-sm">
              <button 
                class="btn"
                :class="viewMode === 'grid' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="viewMode = 'grid'"
              >
                <i class="bi bi-grid-3x3 me-1"></i>Grid
              </button>
              <button 
                class="btn"
                :class="viewMode === 'map' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="viewMode = 'map'"
              >
                <i class="bi bi-map me-1"></i>Map
              </button>
              <button 
                class="btn"
                :class="viewMode === 'list' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="viewMode = 'list'"
              >
                <i class="bi bi-list-ul me-1"></i>List
              </button>
            </div>

            <div class="btn-group btn-group-sm">
              <button 
                class="btn btn-outline-secondary"
                :class="{ active: filter === 'all' }"
                @click="filter = 'all'"
              >
                All
              </button>
              <button 
                class="btn btn-outline-success"
                :class="{ active: filter === 'ok' }"
                @click="filter = 'ok'"
              >
                <i class="bi bi-check-circle me-1"></i>Safe
              </button>
              <button 
                class="btn btn-outline-warning"
                :class="{ active: filter === 'warning' }"
                @click="filter = 'warning'"
              >
                <i class="bi bi-exclamation-triangle me-1"></i>Caution
              </button>
              <button 
                class="btn btn-outline-danger"
                :class="{ active: filter === 'danger' }"
                @click="filter = 'danger'"
              >
                <i class="bi bi-x-circle me-1"></i>No Pour
              </button>
            </div>
          </div>

          <!-- Grid View -->
          <div v-if="hasRun && !isRunning && viewMode === 'grid'" class="results-view">
            <ExceptionDateGrid
              :results="locationResults"
              :dates="allDates"
              :filter="filter"
              :unitGroup="unitGroup"
              primaryField="tempmax"
              :showSecondary="true"
              secondaryField="precipprob"
              cornerLabel="Job Site"
              okLabel="Safe to Pour"
              warningLabel="Hot/Cold Weather"
              dangerLabel="Do Not Pour"
              maxHeight="450px"
              @cell-click="handleCellClick"
            />
          </div>

          <!-- Map View -->
          <div v-if="hasRun && !isRunning && viewMode === 'map'" class="results-view">
            <ExceptionMapView
              :results="filteredResults"
              :isLoading="isRunning"
              height="500px"
              dangerLabel="No Pour"
              @view-details="showSiteDetails"
            />
          </div>

          <!-- List View -->
          <div v-if="hasRun && !isRunning && viewMode === 'list'" class="results-view">
            <div class="list-group">
              <div 
                v-for="result in filteredResults" 
                :key="result.id"
                class="list-group-item list-group-item-action"
                :class="{
                  'list-group-item-success': result.maxSeverity === 'ok',
                  'list-group-item-warning': result.maxSeverity === 'warning',
                  'list-group-item-danger': result.maxSeverity === 'danger'
                }"
                @click="showSiteDetails(result)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="mb-1">{{ result.name || result.location }}</h6>
                    <small v-if="result.name" class="text-muted">{{ result.location }}</small>
                  </div>
                  <div class="text-end">
                    <span v-if="result.status === 'ok'" class="badge" :class="{
                      'bg-success': result.maxSeverity === 'ok',
                      'bg-warning text-dark': result.maxSeverity === 'warning',
                      'bg-danger': result.maxSeverity === 'danger'
                    }">
                      {{ getStatusLabel(result) }}
                    </span>
                    <span v-else class="badge bg-secondary">{{ result.status }}</span>
                    <div v-if="result.exceptionCount > 0" class="small mt-1">
                      {{ result.exceptionCount }} day(s) with issues
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!hasRun" class="empty-state text-center py-5">
            <i class="bi bi-building fs-1 text-muted mb-3 d-block"></i>
            <h5 class="text-muted">Ready to Plan Your Pours</h5>
            <p class="text-muted">
              Add your job sites, set your pour conditions, and click "Find Pour Windows" to check the weather.
            </p>
          </div>
        </div>
      </div>
    </template>

    <!-- Site Detail Modal -->
    <Teleport to="#vcpopovercontainer">
      <div v-if="selectedSite" class="modal fade show d-block" tabindex="-1">
        <div class="modal-backdrop fade show" @click="selectedSite = null"></div>
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" style="position: relative; z-index: 1055;">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="bi bi-building me-2"></i>
                {{ selectedSite.name || selectedSite.location }}
              </h5>
              <button type="button" class="btn-close" @click="selectedSite = null"></button>
            </div>
            <div class="modal-body">
              <!-- Status Banner -->
              <div class="alert mb-3" :class="{
                'alert-success': selectedSite.maxSeverity === 'ok',
                'alert-warning': selectedSite.maxSeverity === 'warning',
                'alert-danger': selectedSite.maxSeverity === 'danger'
              }">
                <div class="d-flex align-items-center">
                  <i class="bi me-2 fs-4" :class="{
                    'bi-check-circle': selectedSite.maxSeverity === 'ok',
                    'bi-exclamation-triangle': selectedSite.maxSeverity === 'warning',
                    'bi-x-circle': selectedSite.maxSeverity === 'danger'
                  }"></i>
                  <div>
                    <strong>{{ getStatusLabel(selectedSite) }}</strong>
                    <div class="small">
                      {{ selectedSite.exceptionCount }} day(s) with pour restrictions
                    </div>
                  </div>
                </div>
              </div>

              <!-- Daily Forecast Table -->
              <div class="table-responsive">
                <table class="table table-sm table-hover">
                  <thead class="table-light">
                    <tr>
                      <th>Date</th>
                      <th>High</th>
                      <th>Low</th>
                      <th>Rain %</th>
                      <th>Wind</th>
                      <th>Snow</th>
                      <th>Status</th>
                      <th>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      v-for="day in selectedSite.days" 
                      :key="day.datetime"
                      :class="{
                        'table-success': day.severity === 'ok',
                        'table-warning': day.severity === 'warning',
                        'table-danger': day.severity === 'danger'
                      }"
                    >
                      <td class="fw-medium">{{ formatDate(day.datetime) }}</td>
                      <td>{{ formatValue(day.tempmax, 'temp') }}</td>
                      <td>{{ formatValue(day.tempmin, 'temp') }}</td>
                      <td>{{ Math.round(day.precipprob || 0) }}%</td>
                      <td>{{ formatValue(day.windgust, 'speed') }}</td>
                      <td>{{ day.snow > 0 ? formatValue(day.snow, 'snow') : '-' }}</td>
                      <td>
                        <span class="badge" :class="{
                          'bg-success': day.severity === 'ok',
                          'bg-warning text-dark': day.severity === 'warning',
                          'bg-danger': day.severity === 'danger'
                        }">
                          {{ day.severity === 'ok' ? 'Safe' : day.severity === 'warning' ? 'Caution' : 'No Pour' }}
                        </span>
                      </td>
                      <td>
                        <small v-if="day.matchedCriteria?.length" class="text-muted">
                          {{ getDayIssues(day) }}
                        </small>
                        <span v-else class="text-muted">-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="selectedSite = null">Close</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useWeatherExceptionEngine } from '../useWeatherExceptionEngine.js';
import { createCriterion } from '../exceptionCriteriaPresets.js';
import { ExceptionLocationPanel, ExceptionMapView, ExceptionResultsSummary, ExceptionDateGrid } from '../core';

export default {
  name: 'ConcretePourTracker',

  components: {
    ExceptionLocationPanel,
    ExceptionMapView,
    ExceptionResultsSummary,
    ExceptionDateGrid
  },

  props: {
    /** Optional override for the API server root URL */
    serverRoot: {
      type: String,
      default: null
    }
  },


  setup(props) {
    // Initialize the weather exception engine
    const engine = useWeatherExceptionEngine({
      storageKey: 'concrete-pour',
      defaultStartPeriod: 'next7days',
      defaultUnitGroup: 'metric',
      maxConcurrentRequests: 3,
      serverRoot: props.serverRoot
    });

    // Initialize session on mount
    onMounted(() => {
      engine.initSession();
    });

    return {
      // Engine state
      ...engine
    };
  },

  data() {
    return {
      // View state
      viewMode: 'grid',
      filter: 'all',
      showAdvancedSettings: false,
      selectedSite: null,

      // Date period options
      datePeriods: [
        { value: 'next3days', label: '3 Days' },
        { value: 'next7days', label: '7 Days' },
        { value: 'next10days', label: '10 Days' },
        { value: 'next14days', label: '14 Days' }
      ],

      // Thresholds (in current unit group)
      thresholds: {
        minTemp: 5,       // Minimum safe pour temp (°C)
        maxTemp: 30,      // Maximum safe pour temp (°C)
        maxPrecipProb: 30,// Maximum acceptable rain probability
        maxWindGust: 40,  // Maximum wind gust (km/h)
        noSnow: true,     // Require no snow
        hotWeatherTemp: 30,  // Hot weather warning threshold (°C)
        coldWeatherTemp: 5,  // Cold weather alert threshold (°C)
        stormRisk: true,     // Check for severe weather
        stormRiskThreshold: 30  // Severe risk threshold (0-100)
      }
    };
  },

  computed: {
    tempUnit() {
      return this.unitGroup === 'us' ? '°F' : '°C';
    },

    windUnit() {
      return this.unitGroup === 'us' ? 'mph' : 'km/h';
    },

    dateRangeDisplay() {
      const period = this.datePeriods.find(p => p.value === this.startPeriod);
      return period ? period.label.toLowerCase() : 'selected period';
    },

    filteredResults() {
      if (this.filter === 'all') return this.locationResults;
      return this.locationResults.filter(r => {
        if (this.filter === 'ok') return r.maxSeverity === 'ok';
        if (this.filter === 'warning') return r.maxSeverity === 'warning';
        if (this.filter === 'danger') return r.maxSeverity === 'danger';
        return true;
      });
    }
  },

  watch: {
    unitGroup(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.convertThresholds(oldVal, newVal);
      }
    }
  },

  mounted() {
    // Set initial thresholds based on unit group
    this.initializeThresholds();
    // Build initial criteria
    this.updateCriteria();
  },

  methods: {
    initializeThresholds() {
      if (this.unitGroup === 'us') {
        this.thresholds.minTemp = 40;
        this.thresholds.maxTemp = 86;
        this.thresholds.maxWindGust = 25;
        this.thresholds.hotWeatherTemp = 86;
        this.thresholds.coldWeatherTemp = 40;
      } else {
        this.thresholds.minTemp = 5;
        this.thresholds.maxTemp = 30;
        this.thresholds.maxWindGust = 40;
        this.thresholds.hotWeatherTemp = 30;
        this.thresholds.coldWeatherTemp = 5;
      }
    },

    convertThresholds(fromUnit, toUnit) {
      const convertTemp = (val) => {
        if (fromUnit === 'metric' && toUnit === 'us') {
          return Math.round(val * 9/5 + 32);
        } else if (fromUnit === 'us' && toUnit === 'metric') {
          return Math.round((val - 32) * 5/9);
        }
        return val;
      };

      const convertSpeed = (val) => {
        if (fromUnit === 'metric' && toUnit === 'us') {
          return Math.round(val / 1.60934);
        } else if (fromUnit === 'us' && toUnit === 'metric') {
          return Math.round(val * 1.60934);
        }
        return val;
      };

      this.thresholds.minTemp = convertTemp(this.thresholds.minTemp);
      this.thresholds.maxTemp = convertTemp(this.thresholds.maxTemp);
      this.thresholds.hotWeatherTemp = convertTemp(this.thresholds.hotWeatherTemp);
      this.thresholds.coldWeatherTemp = convertTemp(this.thresholds.coldWeatherTemp);
      this.thresholds.maxWindGust = convertSpeed(this.thresholds.maxWindGust);

      this.updateCriteria();
    },

    updateCriteria() {
      const criteria = [];

      // Too cold to pour (DANGER - red)
      criteria.push(createCriterion(
        'tempmin', 'lt', this.thresholds.minTemp, 'days', 
        'Too Cold to Pour',
        { color: 'danger' }
      ));

      // Too hot to pour (DANGER - red)
      criteria.push(createCriterion(
        'tempmax', 'gt', this.thresholds.maxTemp, 'days',
        'Too Hot to Pour',
        { color: 'danger' }
      ));

      // Rain likely (DANGER - red)
      criteria.push(createCriterion(
        'precipprob', 'gt', this.thresholds.maxPrecipProb, 'days',
        'Rain Likely',
        { color: 'danger' }
      ));

      // High winds (WARNING - orange)
      if (this.thresholds.maxWindGust > 0) {
        criteria.push(createCriterion(
          'windgust', 'gt', this.thresholds.maxWindGust, 'days',
          'High Winds',
          { color: 'warning' }
        ));
      }

      // Snow expected (DANGER - red)
      if (this.thresholds.noSnow) {
        criteria.push(createCriterion(
          'snow', 'gt', 0, 'days',
          'Snow Expected',
          { color: 'danger' }
        ));
      }

      // Hot weather warning (WARNING - orange)
      if (this.showAdvancedSettings && this.thresholds.hotWeatherTemp) {
        criteria.push(createCriterion(
          'tempmax', 'gt', this.thresholds.hotWeatherTemp, 'days',
          'Hot Weather Precautions',
          { color: 'warning' }
        ));
      }

      // Cold weather warning (WARNING - orange)
      if (this.showAdvancedSettings && this.thresholds.coldWeatherTemp) {
        criteria.push(createCriterion(
          'tempmin', 'lt', this.thresholds.coldWeatherTemp, 'days',
          'Cold Weather Precautions',
          { color: 'warning' }
        ));
      }

      // Severe weather/storm risk (DANGER - red)
      if (this.showAdvancedSettings && this.thresholds.stormRisk) {
        criteria.push(createCriterion(
          'severerisk', 'gt', this.thresholds.stormRiskThreshold, 'days',
          'Severe Weather Risk',
          { color: 'danger' }
        ));
      }

      this.setCriteria(criteria, 'OR');
    },

    getStatusLabel(result) {
      if (result.status !== 'ok') return 'Error';
      switch (result.maxSeverity) {
        case 'danger': return 'Do Not Pour';
        case 'warning': return 'Pour with Caution';
        default: return 'Safe to Pour';
      }
    },

    getDayIssues(day) {
      if (!day.matchedCriteria?.length) return '';
      
      // Get criterion names
      const issues = [];
      for (const cid of day.matchedCriteria) {
        const criterion = this.activeRuleset?.criteria?.find(c => c.id === cid);
        if (criterion?.name) {
          issues.push(criterion.name);
        }
      }
      return issues.join(', ');
    },

    showSiteDetails(result) {
      this.selectedSite = result;
    },

    handleCellClick({ result, date, day }) {
      this.showSiteDetails(result);
    },

    formatDate(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime + 'T12:00:00');
      return d.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  }
};
</script>

<style scoped>
.concrete-pour-tracker {
  padding: 1rem;
}

.tracker-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #dee2e6;
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.condition-group {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.condition-group:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.empty-state {
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #dee2e6;
}

.results-view {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}
</style>
